/**
 * 后端 API 客户端（需求 §5.1）
 *
 * 设计要点：
 *  · 统一错误体解析：服务端恒返回 `{ error: { code, message, retryable } }`
 *  · 区分「可重试」与「不可重试」：前者自动退避重试，后者直接抛给 UI
 *  · 指数元数据统一从 `/api/instruments` 取，消除前后端两份定义（需求 §F2.1）
 *  · 所有响应类型与 server-go/internal/model/model.go 对齐
 */

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/* ─────────────── 共享类型 ─────────────── */

export type Period = 'D' | 'W' | 'M' | 'Y';
export type NoteScope = 'day' | 'week' | 'month' | 'year';
export type Precision = 'tick' | 'min1' | 'min5' | 'unavailable';

export interface Bar {
  date: string;
  open: number; high: number; low: number; close: number;
  volume: number; amount?: number;
  /** 当日尚未收盘 → 该柱仍可被覆盖 */
  isComplete: boolean;
  /**
   * ⚠️ 以下字段后端**一直在返回**（model.Bar 的 json tag，见
   * server-go/internal/model/model.go），只是此前前端类型没声明，
   * 故各处只能自己用 close 反算涨跌幅。现补齐声明，光标读数与
   * 悬停摘要直接取用，避免与后端口径不一致。
   *
   * 标可选：接口有则用、无则降级（如本地 mock 或未来裁剪响应体）。
   */
  pctChange?: number;
  change?: number;
  turnover?: number;
  /** 仅周/月/年线有值，日线为 null（需求 §4.6 周期聚合） */
  barStartDate?: string | null;
  barEndDate?: string | null;
}

/**
 * /api/news 响应。
 *
 * `tradeDate` / `naturalDates` / `attributed` 仅在按交易日查询时有值 ——
 * 服务端以 omitempty 追加，属**向后兼容**的扩展，老调用方可忽略。
 */
export interface NewsResponse {
  total: number;
  items: NewsItem[];
  code: string | null;
  /** 本次结果对应的交易日（K 线联动时） */
  tradeDate?: string;
  /** 本次覆盖到的自然日（含休市日），升序；首项可能早于 tradeDate */
  naturalDates?: string[];
  /** 是否发生过休市归属，前端据此标注「10-01~10-07 休市，已归入 10-08」 */
  attributed?: boolean;
}

export interface KlineResponse {
  code: string; period: Period; bars: Bar[];
  source: string; cached: boolean;
}

/**
 * 快照。字段名以 server-go/internal/model/model.go 的 Snapshot 为准：
 * 注意是 `pctChange` 而非 `changePct` —— 早前前端写成 changePct，
 * 导致 changePct 恒为 undefined，切换指数时 `undefined.toFixed()` 直接崩。
 */
export interface Quote {
  code: string; name: string;
  price: number;
  /** 涨跌额 */
  change: number;
  /** 涨跌幅（百分数，如 -0.57 表示 -0.57%） */
  pctChange: number;
  open: number; high: number; low: number; prevClose: number;
  volume?: number; amount?: number; turnover?: number;
  pe?: number; amplitude?: number;
  totalCap?: number;
  timestamp?: string;
  source: string; cached: boolean; stale: boolean;
}

export interface MinutePoint {
  time: string; price: number; cumVolume: number; cumAmount: number;
}

  export interface MinuteResponse {
    code: string; date: string; points: MinutePoint[];
    /** tick=真实分时；min1/min5=重建；unavailable=超窗口，正确拒绝 */
    precision: Precision;
    source: string;
  }

  /** 5 日分时里的一天；prevClose 是该日昨收，前端据此做「各自 0%」归一化 */
  export interface MinuteDay {
    date: string; precision: Precision; prevClose: number; points: MinutePoint[];
  }

  export interface MinuteDaysResponse {
    code: string;
    /** 按日期升序（最旧在前） */
    days: MinuteDay[];
  }

export interface NewsItem {
  id: string; date: string; publishedAt: string; title: string;
  summary?: string; url?: string; media?: string; image?: string;
  starred: boolean;
}

export interface ValuationPoint {
  date: string;
  pe1?: number; pe2?: number;
  divYield1?: number; divYield2?: number;
  pe2Rank?: number | null; divRank?: number | null;
}

export interface ValuationStatus {
  code: string; supported: boolean;
  latestDate: string | null; expectedDate: string | null;
  count: number; lag: number;
  state: 'ok' | 'lagging' | 'missing' | 'unsupported';
  gaps: { date: string; reason: string }[];
}

/**
 * 一篇**日**笔记。
 *
 * id 是身份：每日可多篇，「用户+指数+日期」不再唯一。
 * 情绪/预期/实际已移出本表 —— 见 Prediction（每天一条）。
 */
export interface Note {
  id: number; code: string; scope: string; scopeKey: string; content: string;
  tags: string[]; createdAt: string; updatedAt: string;
}

/** 某一天的涨跌预测，与笔记解耦：每天一条。 */
export interface Prediction {
  code: string; date: string;
  sentiment: string | null; expectation: string; outcome: string;
  createdAt: string; updatedAt: string;
}

export interface CalendarCell {
  date: string;
  isTradingDay: boolean; hasKline: boolean; hasMinute: boolean;
  newsCount: number;
  /** 当日标星新闻数 —— 月历金点只看它，不看 newsCount */
  starCount: number;
  /** 当日（当前用户·当前指数）日笔记篇数 —— 蓝点 */
  noteCount: number;
  /** 当日有内容的预测数 —— 紫点 */
  predCount: number;
  /** 已收盘的交易日才允许进入复盘 */
  reviewable: boolean;
  isLatest: boolean;
}

/** ⚠️ 以 /api/status 实际响应为准（见 server-go/internal/api/auth.go（buildHealth）），勿凭假设写类型 */
export interface HealthSummary {
  serverOnline: boolean;
  /** 各市场最新交易日，key 为大写市场码 */
  lastTradingDay: Record<string, string | undefined>;
  /** 估值落后交易日数 —— L3 告警判据（>0 即不可逆数据正在流失） */
  valuationLagDays: number;
  /** 最近一次新闻同步时间 */
  newsLastSync: string | null;
  gaps: { code: string; kind: string; count: number }[];
  /** 一行摘要，已本地化，可直接展示 */
  summary: string;
}

export interface GapsResponse {
  items: {
    code: string; name: string; missingCount: number; missing: string[];
    recoverable: boolean; from: string; to: string;
  }[];
  at: string;
}

export interface SyncLogRow {
  id: number; kind: string; code: string | null; trigger: string;
  startedAt: string; finishedAt: string | null;
  expected: number | null; got: number | null;
  status: string; message: string | null;
}

export interface HealthDetail {
  code: string; name: string; market: string;
  klineMaxDate: string | null; klineRows: number;
  minuteDates: number; newsCount: number;
  valuationLatest: string | null; valuationState: string; valuationLag: number;
}

export interface Instrument {
  code: string; name: string; market: 'CN' | 'HK';
  klineSymbol: string; eastmoneyOnly: boolean;
  supportValuation: boolean;
}

/* ─────────────── 错误 ─────────────── */

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface GetOpts {
  /** 内部使用，勿传 */
  _retries?: number;
  signal?: AbortSignal;
}

async function request<T>(path: string, init?: RequestInit & GetOpts): Promise<T> {
  // 会话走 Cookie，必须显式带上（默认 same-origin 会带，但显式更清晰且防将来改 origin）
  const retries = init?._retries ?? 2;
  const { _retries: _omit, ...rest } = init ?? {};
  void _omit;

  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${BASE}${path}`, {
        ...rest,
        credentials: 'same-origin',
        headers: { ...(rest.headers ?? {}) },
      });
    } catch (e) {
      // 网络层失败：可重试
      lastErr = new ApiError('NETWORK_ERROR',
        e instanceof Error ? e.message : '网络不可达', true, 0);
      if (attempt < retries) { await sleep(400 * 2 ** attempt); continue; }
      throw lastErr;
    }

    if (res.ok) return (await res.json()) as T;

    const body = (await res.json().catch(() => null)) as
      | { error?: { code: string; message: string; retryable: boolean } }
      | null;
    const err = new ApiError(
      body?.error?.code ?? 'UNKNOWN',
      body?.error?.message ?? `HTTP ${res.status}`,
      body?.error?.retryable ?? res.status >= 500,
      res.status,
    );

    // 401 = 会话失效（过期 / 改密 / 被停用）。广播给 AuthProvider 统一处理，
    // 不在这里直接跳登录 —— 请求层不该决定导航。
    if (res.status === 401 && !path.startsWith('/auth/')) {
      window.dispatchEvent(new CustomEvent('stocknote:unauthorized'));
    }
    // 不可重试 → 立刻上抛（参数错误重试无意义）
    if (!err.retryable || attempt === retries) throw err;
    lastErr = err;
    await sleep(400 * 2 ** attempt);
  }
  throw lastErr;
}

const get = <T>(p: string, o?: GetOpts) => request<T>(p, { method: 'GET', ...o });
const post = <T>(p: string, body?: unknown) =>
  request<T>(p, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body ?? {}) });
const put = <T>(p: string, body?: unknown) =>
  request<T>(p, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body ?? {}) });
const del = <T>(p: string) => request<T>(p, { method: 'DELETE' });

const qs = (o: Record<string, string | number | boolean | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

/* ─────────────── 端点 ─────────────── */

export const api = {
  health: () => get<{ ok: boolean; at: string }>('/health'),

  /* ── 鉴权 ── */
  me: () => get<{ user: { id: number; username: string; displayName: string | null; role: 'user' | 'admin' } }>('/auth/me'),
  login: (username: string, password: string) =>
    post<{ user: { id: number; username: string; displayName: string | null; role: 'user' | 'admin' }; expiresAt: string }>(
      '/auth/login', { username, password }),
  logout: () => post<{ ok: boolean }>('/auth/logout'),
  changePassword: (current: string, next: string) =>
    post<{ ok: boolean; reauth: boolean }>('/auth/password', { current, next }),

  instruments: () => get<{ items: Instrument[] }>('/instruments'),

  quotes: (codes: string[]) =>
    get<{ items: Quote[]; source: string }>(`/quote${qs({ codes: codes.join(',') })}`),

  kline: (code: string, period: Period = 'D', limit = 2000) =>
    get<KlineResponse>(`/kline${qs({ code, period, limit })}`),

  periods: (code: string, period: Period = 'W', limit = 2000) =>
    get<{ code: string; period: Period; items: { date: string; scopeKey: string }[] }>(
      `/periods${qs({ code, period, limit })}`),

  tradingDays: (code: string) =>
    get<{ code: string; dates: string[]; latest: string | null }>(`/trading-days${qs({ code })}`),

    minute: (code: string, date?: string) =>
      get<MinuteResponse>(`/minute${qs({ code, date })}`),

    /** 5 日分时：最近 n 个交易日的分时，每日带昨收，供水平带渲染 */
    minuteDays: (code: string, n = 5) =>
      get<MinuteDaysResponse>(`/minute/days${qs({ code, n })}`),

  /** 新闻：code 仅用于日历联动，返回与指数无关（全局共享） */
  /**
   * 新闻查询。
   *
   * `tradeDate` 与 `code` 一起传时走「K 线联动」入口：按交易日取新闻，
   * 自动把归属到该交易日的休市日新闻一并纳入（需求 §F2.9）。
   * 服务端只在响应层归属，news.date 始终是原始发布自然日。
   */
  news: (o: { from?: string; to?: string; starred?: boolean; q?: string; limit?: number; offset?: number; code?: string; tradeDate?: string } = {}) =>
    get<NewsResponse>(`/news${qs(o)}`),

  newsDates: (from?: string, to?: string) =>
    get<{ items: { date: string; count: number }[] }>(`/news/dates${qs({ from, to })}`),

  setStarred: (id: string, starred: boolean) =>
    post<{ id: string; starred: boolean }>(`/news/${encodeURIComponent(id)}/starred`, { starred }),

  valuation: (code: string, limit = 20) =>
    get<{
      code: string; supported: boolean; items: ValuationPoint[];
      latest: ValuationPoint | null; status: ValuationStatus;
      /** 中证只保留 20 个交易日 —— UI 不得承诺全历史 */
      windowNote: { window: number; note: string };
    }>(`/valuation${qs({ code, limit })}`),

  valuationStatuses: () =>
    get<{ items: ValuationStatus[]; alert: boolean }>('/valuation/status'),

  /** 区间内的日笔记：点周K柱即看到当周所有日笔记，月/年同理 */
  notes: (code: string, from: string, to: string, limit?: number) =>
    get<{ code: string; items: Note[] }>(`/notes/${code}${qs({ from, to, limit })}`),

  note: (code: string, id: number) => get<Note>(`/notes/${code}/${id}`),

  /**
   * 新建一篇日笔记（每日可多篇，故以 id 而非日期为身份）。
   *
   * ⚠️ 只有新建需要日期：更新的归属（code / scope_key）由服务端以库中现值为准，
   *    不接受客户端传入 —— 传错会把笔记挪到另一天。分开两个函数而非
   *    `saveNote(code, date, patch)` 是为了不留下「date 对更新无效」的陷阱。
   */
  createNote: (code: string, date: string,
    patch: { content?: string; tags?: string[] }) =>
    put<Note>(`/notes/${code}/new/${date}`, patch),

  /** 更新某篇；只改正文与标签，不改归属 */
  updateNote: (code: string, id: number,
    patch: { content?: string; tags?: string[] }) =>
    put<Note>(`/notes/${code}/${id}`, { id, ...patch }),

  deleteNote: (code: string, id: number) => del<{ ok: boolean }>(`/notes/${code}/${id}`),

  predictions: (code: string, from: string, to: string, limit?: number) =>
    get<{ code: string; items: Prediction[] }>(`/predictions/${code}${qs({ from, to, limit })}`),

  prediction: (code: string, date: string) => get<Prediction>(`/predictions/${code}/${date}`),

  savePrediction: (code: string, date: string,
    patch: { sentiment?: string | null; expectation?: string; outcome?: string }) =>
    put<Prediction>(`/predictions/${code}/${date}`, patch),

  deletePrediction: (code: string, date: string) =>
    del<{ ok: boolean }>(`/predictions/${code}/${date}`),

  searchNotes: (q: string, o: { code?: string; tag?: string } = {}) =>
    get<{ q: string; items: Note[] }>(`/notes${qs({ q, ...o })}`),

  noteTags: () => get<{ items: { tag: string; count: number }[] }>('/notes/tags/all'),

  calendar: (code: string, from?: string, to?: string) =>
    get<{ code: string; from: string; to: string; latest: string | null; items: CalendarCell[] }>(
      `/calendar${qs({ code, from, to })}`),

  calendarMarkets: () =>
    get<{
      today: string;
      markets: {
        market: string; latestTradingDay: string | null;
        isTodayTradingDay: boolean; calendarReady: boolean;
        instrumentsWithCalendar: number; instrumentsTotal: number;
      }[];
    }>('/calendar/markets'),

  status: () => get<HealthSummary>('/status'),
  statusDetail: () => get<{ items: HealthDetail[] }>('/status/detail'),

  syncGaps: () => get<{ items: GapsResponse['items']; at: string }>('/sync/gaps'),
  syncLog: (o: { code?: string; kind?: string; limit?: number } = {}) =>
    get<{ items: SyncLogRow[] }>(`/sync/log${qs(o)}`),
  syncStats: () => get<{ dbKb: number; tables: { table: string; rows: number }[]; valuationAlert: boolean }>('/sync/stats'),
  syncKline: (code?: string) => post<{ items: (SyncLogRow & { code: string; inserted?: number; error?: string })[] }>(
    '/sync/kline', { code }),
  syncNews: (o: { date?: string; from?: string; to?: string } = {}) => post<unknown>('/news/sync', o),
  syncValuation: (code?: string) => post<unknown>('/valuation/sync', { code }),
  archive: () => post<unknown>('/sync/archive'),
} as const;

export default api;
/**
 * 手动同步：仅管理员可用（服务端对普通账号返回 403）。
 *
 * 之所以收紧到 admin：这些端点会向腾讯/东财/中证发起**大量**上游请求，
 * 而东财一旦封禁需 10 分钟以上才恢复。普通账号误触或登录态泄露都会
 * 造成实质影响。日常更新由服务内置定时任务完成，无需手动触发。
 */
export const manualSync = {
  kline: (code?: string) => api.syncKline(code),
  minute: (code: string, date?: string) =>
    post<{ code: string; precision: string; points: number }>('/sync/minute', { code, date }),
  valuation: (code?: string) => api.syncValuation(code),
  news: (o: { date?: string; from?: string; to?: string } = {}) => api.syncNews(o),
  archive: () => api.archive(),
};
